/**
 * MissionLogger — records every gameplay event with enough detail for 3D replay.
 *
 * Logs are saved to the server database automatically:
 * - Periodically during gameplay (every 30s)
 * - On mission end
 * - On page unload
 *
 * Logs can also be downloaded locally via Ctrl+Shift+L or the debrief screen.
 */

import type {
  EncounterState,
  SpatialState,
  GameIntent,
  GameMasterResponse,
  DialogueLine,
  StateUpdates,
  AlertLevel,
  ViewscreenMode,
  HailState,
  Vec3,
} from "../types/game";
import type { ActiveEffect } from "./SceneCommandExecutor";

// ── Event types ──

interface BaseEvent {
  /** Milliseconds since mission start */
  t: number;
}

export interface SpatialSnapshotEvent extends BaseEvent {
  type: "spatial";
  playerShip: {
    position: Vec3;
    heading: Vec3;
    speed: string;
    shields: "up" | "down";
    orbitTarget?: string;
  };
  entities: {
    id: string;
    position: Vec3;
    shields?: "up" | "down";
    condition?: string;
    cloaked?: boolean;
  }[];
}

export interface IntentEvent extends BaseEvent {
  type: "intent";
  intent: GameIntent;
}

export interface EffectEvent extends BaseEvent {
  type: "effect";
  effect: {
    id: string;
    effectType: ActiveEffect["type"];
    from?: Vec3;
    to?: Vec3;
    position?: Vec3;
    color?: string;
    duration: number;
    size?: number;
  };
}

export interface DialogueEvent extends BaseEvent {
  type: "dialogue";
  speaker: string;
  role: string;
  text: string;
  sidebar?: boolean;
}

export interface PlayerActionEvent extends BaseEvent {
  type: "player_action";
  action: string;
}

export interface GMResponseEvent extends BaseEvent {
  type: "gm_response";
  dialogueCount: number;
  intentCount: number;
  timedEventCount: number;
  stateUpdateKeys: string[];
  encounterResolved?: boolean;
  resolutionType?: string;
  resolutionSummary?: string;
}

export interface StateChangeEvent extends BaseEvent {
  type: "state_change";
  updates: StateUpdates;
}

export interface OrderEvent extends BaseEvent {
  type: "order";
  action: "add" | "complete" | "cancel";
  orderId: string;
  label: string;
  duration?: number;
}

export interface AlertEvent extends BaseEvent {
  type: "alert";
  level: AlertLevel;
}

export interface ViewscreenEvent extends BaseEvent {
  type: "viewscreen";
  mode: ViewscreenMode;
}

export interface HailEvent extends BaseEvent {
  type: "hail";
  state: HailState;
}

export interface MissionEndEvent extends BaseEvent {
  type: "mission_end";
  resolutionType: string;
  summary: string;
}

export interface SceneCommandEvent extends BaseEvent {
  type: "scene_command";
  action: string;
  params: Record<string, any>;
}

export type MissionEvent =
  | SpatialSnapshotEvent
  | IntentEvent
  | EffectEvent
  | DialogueEvent
  | PlayerActionEvent
  | GMResponseEvent
  | StateChangeEvent
  | OrderEvent
  | AlertEvent
  | ViewscreenEvent
  | HailEvent
  | MissionEndEvent
  | SceneCommandEvent;

// ── Log format ──

export interface MissionLog {
  version: 1;
  sessionId: string;
  startedAt: number;
  endedAt?: number;
  initialEncounter: EncounterState;
  events: MissionEvent[];
}

// ── Config ──
const SNAPSHOT_INTERVAL_MS = 500;
const SAVE_INTERVAL_MS = 30_000;

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

export class MissionLogger {
  private log: MissionLog;
  private startTime: number;
  private lastSnapshotTime = 0;
  private lastSaveTime = 0;
  private _active = true;
  private _saving = false;

  constructor(sessionId: string, initialEncounter: EncounterState) {
    this.startTime = Date.now();
    this.lastSaveTime = Date.now();
    this.log = {
      version: 1,
      sessionId,
      startedAt: this.startTime,
      initialEncounter: structuredClone(initialEncounter),
      events: [],
    };
  }

  get active() { return this._active; }

  private elapsed(): number {
    return Date.now() - this.startTime;
  }

  private push(event: MissionEvent) {
    if (!this._active) return;
    this.log.events.push(event);
  }

  // ── Spatial snapshots (called from game loop) ──

  snapshotSpatial(spatial: SpatialState) {
    const now = Date.now();
    if (now - this.lastSnapshotTime < SNAPSHOT_INTERVAL_MS) return;
    this.lastSnapshotTime = now;

    this.push({
      type: "spatial",
      t: this.elapsed(),
      playerShip: {
        position: { ...spatial.playerShip.position },
        heading: { ...spatial.playerShip.heading },
        speed: spatial.playerShip.speed,
        shields: spatial.playerShip.shields,
        orbitTarget: spatial.playerShip.orbitTarget,
      },
      entities: spatial.entities.map(e => ({
        id: e.id,
        position: { ...e.position },
        shields: e.shields,
        condition: e.condition,
        cloaked: e.cloaked,
      })),
    });

    // Periodic auto-save to server
    if (now - this.lastSaveTime >= SAVE_INTERVAL_MS) {
      this.saveToServer();
    }
  }

  // ── Intents ──

  logIntent(intent: GameIntent) {
    this.push({ type: "intent", t: this.elapsed(), intent: structuredClone(intent) });
  }

  logIntents(intents: GameIntent[]) {
    for (const intent of intents) this.logIntent(intent);
  }

  // ── Effects ──

  logEffect(effect: ActiveEffect) {
    this.push({
      type: "effect",
      t: this.elapsed(),
      effect: {
        id: effect.id,
        effectType: effect.type,
        from: effect.from ? { ...effect.from } : undefined,
        to: effect.to ? { ...effect.to } : undefined,
        position: effect.position ? { ...effect.position } : undefined,
        color: effect.color,
        duration: effect.duration,
        size: effect.size,
      },
    });
  }

  // ── Scene commands ──

  logSceneCommand(action: string, params: Record<string, any>) {
    this.push({
      type: "scene_command",
      t: this.elapsed(),
      action,
      params: structuredClone(params),
    });
  }

  // ── Dialogue ──

  logDialogue(line: DialogueLine) {
    this.push({
      type: "dialogue",
      t: this.elapsed(),
      speaker: line.speaker,
      role: line.role,
      text: line.text,
      sidebar: line.sidebar,
    });
  }

  // ── Player action ──

  logPlayerAction(action: string) {
    this.push({ type: "player_action", t: this.elapsed(), action });
  }

  // ── GM response metadata ──

  logGMResponse(response: GameMasterResponse) {
    this.push({
      type: "gm_response",
      t: this.elapsed(),
      dialogueCount: response.dialogue?.length ?? 0,
      intentCount: response.intents?.length ?? 0,
      timedEventCount: response.timedEvents?.length ?? 0,
      stateUpdateKeys: response.stateUpdates ? Object.keys(response.stateUpdates) : [],
      encounterResolved: response.encounterResolved,
      resolutionType: response.resolutionType,
      resolutionSummary: response.resolutionSummary,
    });
  }

  // ── State changes ──

  logStateChange(updates: StateUpdates) {
    this.push({ type: "state_change", t: this.elapsed(), updates: structuredClone(updates) });
  }

  // ── Orders ──

  logOrder(action: "add" | "complete" | "cancel", orderId: string, label: string, duration?: number) {
    this.push({ type: "order", t: this.elapsed(), action, orderId, label, duration });
  }

  // ── Alert level ──

  logAlert(level: AlertLevel) {
    this.push({ type: "alert", t: this.elapsed(), level });
  }

  // ── Viewscreen mode ──

  logViewscreen(mode: ViewscreenMode) {
    this.push({ type: "viewscreen", t: this.elapsed(), mode: structuredClone(mode) });
  }

  // ── Hail state ──

  logHail(state: HailState) {
    this.push({ type: "hail", t: this.elapsed(), state: { ...state } });
  }

  // ── Mission end ──

  logMissionEnd(resolutionType: string, summary: string) {
    this.log.endedAt = Date.now();
    this.push({ type: "mission_end", t: this.elapsed(), resolutionType, summary });
    this._active = false;
    this.saveToServer();
  }

  // ── Server persistence ──

  /** Save the current log to the server. Non-blocking, fire-and-forget. */
  saveToServer() {
    if (this._saving) return;
    this._saving = true;
    this.lastSaveTime = Date.now();

    const payload = JSON.stringify({
      sessionId: this.log.sessionId,
      log: this.log,
    });

    fetch(`${API_BASE}/api/mission-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
    })
      .then(res => {
        if (!res.ok) console.warn(`[MissionLogger] Save failed: ${res.status}`);
      })
      .catch(err => {
        console.warn("[MissionLogger] Save error:", err);
      })
      .finally(() => {
        this._saving = false;
      });
  }

  /** Synchronous save via sendBeacon (for page unload). */
  saveBeacon() {
    const payload = JSON.stringify({
      sessionId: this.log.sessionId,
      log: this.log,
    });
    navigator.sendBeacon(`${API_BASE}/api/mission-log`, new Blob([payload], { type: "application/json" }));
  }

  // ── Export ──

  getLog(): MissionLog {
    return this.log;
  }

  toJSON(): string {
    return JSON.stringify(this.log);
  }

  /** Download log as a local JSON file. */
  download() {
    const blob = new Blob([this.toJSON()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `mission-log-${this.log.sessionId.slice(0, 8)}-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Total number of events recorded */
  get eventCount(): number {
    return this.log.events.length;
  }
}
